// 本地錄影（NVR / Kinio Station 硬碟、SD 卡）在某一刻有沒有錄影（VMS UX 評審第九輪 R9-P0-2、B9-03；SPEC #vms-web-290 第九輪收尾）：
// 用跟回放時間軸同一份覆蓋資料（pb.timeline，那一刻所在的日子前後各一天，同 PlaybackPage 的查法），
// 不拿縮圖（pb.thumb）成功與否當「有沒有錄影」——縮圖失敗不等於沒錄到（第九輪：NVR631 / CH4 回放有 NVR 硬碟 13:51–21:00，同一時刻卻寫「沒有錄影」）。
import { KinioError, localTimeline } from '@kinio/web-sdk';
import { addDays, zonedDayKey } from './tz';
import { serialByHost } from './hostQueue';

/** yes = 那一刻有錄影；no = 查到了、那一刻沒有；unknown = 查不到（離線、逾時、失敗）；absent = 設備沒有本地錄影（不支援） */
export type LocalCoverage = 'yes' | 'no' | 'unknown' | 'absent';

type Seg = { start: number; end: number; types?: number };
type TimelineFn = (api: string, homeId: string, device: string, date: string, ch?: number) => Promise<Record<string, Seg[]>>;

/** 設備不支援（沒有 SD / 舊韌體）→ 這個來源不存在；其他（逾時、離線）→ 不知道。同 PlaybackPage。 */
export function localUnsupported(e: unknown): boolean {
  const msg = e instanceof KinioError ? e.message : String(e);
  return /unknown (method|op)|not[_ ]?(supported|implemented|found)|no[_ ]?sd|method_not_found/i.test(msg);
}

const TTL_MS = 60_000;
const cache = new Map<string, { at: number; p: Promise<Record<string, Seg[]>> }>();

/** 測試用 */
export function resetLocalCoverageCache(): void {
  cache.clear();
}

function dayTimeline(fn: TimelineFn, api: string, homeId: string, device: string, date: string, ch: number, now: number): Promise<Record<string, Seg[]>> {
  const key = `${api}|${homeId}/${device}/${ch}@${date}`;
  const hit = cache.get(key);
  if (hit && now - hit.at < TTL_MS) return hit.p;
  const p = fn(api, homeId, device, date, ch);
  cache.set(key, { at: now, p });
  p.catch(() => cache.delete(key));
  if (cache.size > 500) cache.delete(cache.keys().next().value as string);
  return p;
}

/**
 * 某一刻（秒）本地有沒有錄影。tz = 設備 / 家戶的時區（pb.timeline 的 date 是設備自己的日子；前後一天一起問，跟回放頁一樣）。
 * 中間那天查不到 → unknown（不支援 → absent）；前後一天查不到不算。
 */
export async function localCoverageAt(api: string, homeId: string, device: string, ch: number, atSec: number, tz: string, fn: TimelineFn = localTimeline, now = Date.now()): Promise<LocalCoverage> {
  const day = zonedDayKey(atSec, tz);
  const days = [addDays(day, -1), day, addDays(day, 1)];
  try {
    const rs = await Promise.all(days.map((d, i) => dayTimeline(fn, api, homeId, device, d, ch, now).catch((e) => (i === 1 ? Promise.reject(e) : {}))));
    const segs = rs.flatMap((r) => (r as Record<string, Seg[]>)[String(ch)] ?? []);
    return segs.some((s) => s.start <= atSec && s.end > atSec) ? 'yes' : 'no';
  } catch (e) {
    return localUnsupported(e) ? 'absent' : 'unknown';
  }
}

/**
 * 一段時間（秒）本地有沒有錄影（多路同步選來源用，SPEC PLAY-03「多路同步的本地來源」）：起點與終點所在的日子（含前後一天）一起問。
 * 一台主機一天只問一次、不帶通道（回所有通道），同一台的幾路共用（2026-10-22 dev：同步頁一次對 lstw-16ch 每個通道各問三天，
 * 設備 RPC 排隊逾時、三路都成了「查不到」）；中間那幾天查不到 → unknown（不支援 → absent）。
 */
export async function localCoverageIn(api: string, homeId: string, device: string, ch: number, fromSec: number, toSec: number, tz: string, fn: TimelineFn = localTimeline, now = Date.now()): Promise<LocalCoverage> {
  const first = zonedDayKey(fromSec, tz);
  const last = zonedDayKey(Math.max(fromSec, toSec - 1), tz);
  const core = first === last ? [first] : [first, last];
  // 同一台主機的 RPC 一次一個（hostQueue；同時送設備會逾時 / 被限流）；Station 一次 pb.timeline 約 6 秒
  const all: TimelineFn = (a, h, dv, date) => serialByHost(`${a}|${h}/${dv}`, () => fn(a, h, dv, date));
  const hit = (rs: Record<string, Seg[]>[]) => rs.flatMap((r) => r[String(ch)] ?? []).some((s) => s.start < toSec && s.end > fromSec);
  try {
    // 先問範圍所在的那幾天（設備時區通常跟家戶一樣）；沒有才問前後一天（設備時區不同時錄影落在隔壁那天）
    const rs = await Promise.all(core.map((d) => dayTimeline(all, api, homeId, device, d, -1, now)));
    if (hit(rs)) return 'yes';
    const side = await Promise.all([addDays(first, -1), addDays(last, 1)].map((d) => dayTimeline(all, api, homeId, device, d, -1, now).catch(() => ({}))));
    return hit(side) ? 'yes' : 'no';
  } catch (e) {
    return localUnsupported(e) ? 'absent' : 'unknown';
  }
}
